import React, { useEffect, useState } from "react"
import PropTypes from "prop-types"
import { useParams, useNavigate } from "react-router-dom"
import {
  Box,
  Container,
  Grid,
  Typography,
  CircularProgress,
  Divider,
  Chip,
} from "@mui/material"
import EditIcon from "@mui/icons-material/Edit"
import DeleteIcon from "@mui/icons-material/Delete"
import InventoryIcon from "@mui/icons-material/Inventory"
import AttachMoneyIcon from "@mui/icons-material/AttachMoney"
import CategoryIcon from "@mui/icons-material/Category"
import {
  MuiPageHeader,
  MuiCard,
  MuiLoadingButton,
  MuiConfirmDialog,
  MuiAlert,
  MuiStatusChip,
} from "../../components/mui"
import { getMaterials, deleteProduct } from "../../helpers/api_helper"

const MuiMaterialOverview = () => {
  const { id } = useParams()
  const navigate = useNavigate()
  document.title = "Material Details | FlexFlow ERP"

  const [material, setMaterial] = useState(null)
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState(null)
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false)
  const [deleting, setDeleting] = useState(false)
  const [deleteSuccess, setDeleteSuccess] = useState(false)

  useEffect(() => {
    const fetchMaterial = async () => {
      if (!id) return

      try {
        const res = await getMaterials(id)
        if (res && res.length > 0) {
          setMaterial(res[0])
        } else {
          setError("Material not found")
        }
      } catch (err) {
        setError("Failed to load material details")
        console.error("Error fetching material:", err)
      } finally {
        setLoading(false)
      }
    }

    fetchMaterial()
  }, [id])

  // Handle edit
  const handleEdit = () => {
    // Navigate to edit page (you can create MuiMaterialEdit.jsx later)
    navigate(`/mui-edit-material/${id}`)
  }

  // Handle delete confirmation
  const handleDeleteClick = () => {
    setDeleteDialogOpen(true)
  }

  const handleDeleteCancel = () => {
    setDeleteDialogOpen(false)
  }

  const handleDeleteConfirm = async () => {
    setDeleting(true)
    try {
      const payload = {
        resource: [{ id: Number(id) }],
      }
      await deleteProduct(payload)
      setDeleteSuccess(true)
      setDeleteDialogOpen(false)
      setTimeout(() => {
        navigate("/mui-materials-list")
      }, 1500)
    } catch (err) {
      setError("Failed to delete material")
      console.error("Error deleting material:", err)
    } finally {
      setDeleting(false)
    }
  }

  // Get stock status
  const getStockStatus = (stock) => {
    if (stock == null || stock === 0) return "outofstock"
    if (stock < 10) return "lowstock"
    return "instock"
  }

  // Format info item
  const InfoItem = ({ label, value, icon }) => (
    <Box sx={{ display: "flex", alignItems: "flex-start", mb: 2 }}>
      {icon && (
        <Box sx={{ mr: 1.5, color: "text.secondary", mt: 0.25 }}>{icon}</Box>
      )}
      <Box>
        <Typography variant="caption" color="text.secondary" display="block">
          {label}
        </Typography>
        <Typography variant="body1" fontWeight={500}>
          {value || "-"}
        </Typography>
      </Box>
    </Box>
  )

  InfoItem.propTypes = {
    label: PropTypes.string.isRequired,
    value: PropTypes.any,
    icon: PropTypes.node,
  }

  if (loading) {
    return (
      <Box
        sx={{
          display: "flex",
          justifyContent: "center",
          alignItems: "center",
          minHeight: 400,
        }}
      >
        <CircularProgress />
      </Box>
    )
  }

  if (error && !material) {
    return (
      <Box sx={{ py: 3 }}>
        <Container maxWidth="lg">
          <MuiAlert severity="error">{error}</MuiAlert>
        </Container>
      </Box>
    )
  }

  return (
    <Box sx={{ py: 3 }}>
      <Container maxWidth="lg">
        <MuiPageHeader
          title={material?.name || "Material Details"}
          subtitle={material?.sku ? `SKU: ${material.sku}` : null}
          breadcrumbs={[
            { label: "Materials", path: "/mui-materials-list" },
            { label: material?.name || "Details" },
          ]}
          actions={
            <Box sx={{ display: "flex", gap: 1 }}>
              <MuiLoadingButton
                variant="outlined"
                color="primary"
                startIcon={<EditIcon />}
                onClick={handleEdit}
              >
                Edit
              </MuiLoadingButton>
              <MuiLoadingButton
                variant="outlined"
                color="error"
                startIcon={<DeleteIcon />}
                onClick={handleDeleteClick}
              >
                Delete
              </MuiLoadingButton>
            </Box>
          }
        />

        {error && (
          <MuiAlert severity="error" onClose={() => setError(null)} sx={{ mb: 3 }}>
            {error}
          </MuiAlert>
        )}

        {deleteSuccess && (
          <MuiAlert severity="success" sx={{ mb: 3 }}>
            Material deleted successfully! Redirecting...
          </MuiAlert>
        )}

        <Grid container spacing={3}>
          {/* Material Info Card */}
          <Grid item xs={12} md={6}>
            <MuiCard title="Material Information" showDivider>
              <Box sx={{ py: 1 }}>
                <InfoItem label="Name" value={material?.name} />
                <InfoItem label="SKU" value={material?.sku} />
                <InfoItem label="Category" value={material?.category} />
                <InfoItem label="Unit" value={material?.unit} />
                <Divider sx={{ my: 2 }} />
                <Typography variant="caption" color="text.secondary" display="block" sx={{ mb: 1 }}>
                  Description
                </Typography>
                <Typography variant="body2" color="text.secondary">
                  {material?.description || "No description provided"}
                </Typography>
              </Box>
            </MuiCard>
          </Grid>

          {/* Stock & Pricing Card */}
          <Grid item xs={12} md={6}>
            <MuiCard title="Stock & Pricing" showDivider>
              <Box sx={{ py: 1 }}>
                {/* Stock Status */}
                <Box sx={{ mb: 3 }}>
                  <Typography variant="caption" color="text.secondary" display="block" sx={{ mb: 1 }}>
                    Stock Status
                  </Typography>
                  <Box sx={{ display: "flex", alignItems: "center", gap: 2 }}>
                    <MuiStatusChip.Inventory
                      status={getStockStatus(material?.stock)}
                      label={
                        getStockStatus(material?.stock) === "instock"
                          ? "In Stock"
                          : getStockStatus(material?.stock) === "lowstock"
                          ? "Low Stock"
                          : "Out of Stock"
                      }
                    />
                    <Typography variant="h6" fontWeight={600}>
                      {material?.stock ?? 0} {material?.unit}
                    </Typography>
                  </Box>
                </Box>

                <Divider sx={{ my: 2 }} />

                {/* Pricing */}
                <Grid container spacing={2}>
                  <Grid item xs={6}>
                    <Box
                      sx={{
                        p: 2,
                        bgcolor: "primary.50",
                        borderRadius: 2,
                        textAlign: "center",
                      }}
                    >
                      <AttachMoneyIcon color="primary" />
                      <Typography variant="caption" color="text.secondary" display="block">
                        Cost Price
                      </Typography>
                      <Typography variant="h6" fontWeight={600}>
                        {material?.costprice != null
                          ? `${Number(material.costprice).toFixed(2)}`
                          : "-"}
                      </Typography>
                    </Box>
                  </Grid>
                  <Grid item xs={6}>
                    <Box
                      sx={{
                        p: 2,
                        bgcolor: "success.50",
                        borderRadius: 2,
                        textAlign: "center",
                      }}
                    >
                      <AttachMoneyIcon color="success" />
                      <Typography variant="caption" color="text.secondary" display="block">
                        Sales Price
                      </Typography>
                      <Typography variant="h6" fontWeight={600}>
                        {material?.salesprice != null
                          ? `${Number(material.salesprice).toFixed(2)}`
                          : "-"}
                      </Typography>
                    </Box>
                  </Grid>
                </Grid>

                <Divider sx={{ my: 2 }} />

                {/* Additional Info */}
                <Grid container spacing={2}>
                  <Grid item xs={6}>
                    <InfoItem label="B2B Sales Price" value={material?.b2bsalesprice} />
                  </Grid>
                  <Grid item xs={6}>
                    <InfoItem
                      label="VAT"
                      value={material?.vat != null ? `${material.vat}%` : null}
                    />
                  </Grid>
                </Grid>
              </Box>
            </MuiCard>
          </Grid>

          {/* Additional Info Card */}
          <Grid item xs={12}>
            <MuiCard title="Additional Details" showDivider collapsible defaultExpanded={false}>
              <Grid container spacing={3}>
                <Grid item xs={12} sm={4}>
                  <InfoItem label="Production Cost" value="--" />
                </Grid>
                <Grid item xs={12} sm={4}>
                  <InfoItem label="Production Hours" value="--" />
                </Grid>
                <Grid item xs={12} sm={4}>
                  <InfoItem label="Material ID" value={material?.id} />
                </Grid>
              </Grid>
            </MuiCard>
          </Grid>
        </Grid>

        {/* Delete Confirmation Dialog */}
        <MuiConfirmDialog.Delete
          open={deleteDialogOpen}
          onClose={handleDeleteCancel}
          onConfirm={handleDeleteConfirm}
          loading={deleting}
          itemName={material?.name}
          title="Delete Material"
          message="Are you sure you want to delete this material? This action cannot be undone."
        />
      </Container>
    </Box>
  )
}

MuiMaterialOverview.propTypes = {
  match: PropTypes.any,
}

export default MuiMaterialOverview
